import DOMPurify from 'dompurify';
import { camelCase, isEmpty, isNil, isString, snakeCase } from 'lodash';

export const Formatter = {
  toTitleCase: (text: string, separator = ' ') => {
    const newText = text.split(separator);
    return newText
      .map((val) => {
        if (val !== '') {
          return val[0].toUpperCase() + val.slice(1).toLowerCase();
        }
      })
      .join(' ');
  },

  //TODO: remove this and use snakeCase from lodash.
  camelCaseToSnakeCase: (text: string) => {
    const result = text.replace(/([A-Z])/g, ' $1').trim();
    return result.split(' ').join('_').toLowerCase();
  },

  /**
   * Recursively converts keys of an object from snake case to camel case.
   * @param obj Response object whose keys we want to convert.
   * @returns Object with keys in camel case.
   */
  convertKeysSnakeToCamelCase: function convertKeysSnakeToCamelCase<ReturnType>(
    obj,
  ): ReturnType {
    if (Array.isArray(obj)) {
      return obj.map(convertKeysSnakeToCamelCase) as unknown as ReturnType;
    } else if (obj !== null && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc, key) => {
        const camelKey = camelCase(key);
        acc[camelKey] = convertKeysSnakeToCamelCase(obj[key]);
        return acc;
      }, {}) as unknown as ReturnType;
    }
    return obj as ReturnType;
  },

  /**
   * Recursively converts keys of an object from camel case to snake case.
   * @param obj Response object whose keys we want to convert.
   * @returns Object with keys in snake case.
   */
  convertKeysCamelCaseToSnakeCase: function convertKeysCamelCaseToSnakeCase<
    ReturnType,
  >(obj): ReturnType {
    if (Array.isArray(obj)) {
      return obj.map(convertKeysCamelCaseToSnakeCase) as unknown as ReturnType;
    } else if (obj !== null && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc, key) => {
        const camelKey = snakeCase(key);
        acc[camelKey] = convertKeysCamelCaseToSnakeCase(obj[key]);
        return acc;
      }, {}) as unknown as ReturnType;
    }
    return obj as ReturnType;
  },
};

export const dateFormatter = (date: Date) =>
  new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);

export const formatNumberUS = (number: number): string =>
  new Intl.NumberFormat('en-US').format(number);

//Ref: https://stackoverflow.com/questions/7738117/html-text-overflow-ellipsis-detection
export const isEllipsis = (ref: React.RefObject<HTMLElement | undefined>) => {
  if (ref.current) {
    const element = ref.current;
    return element.offsetWidth < element.scrollWidth;
  }
  return false;
};

/**
 * Sanitizes HTML and returns plain text using DOMPurify.
 * Uses RETURN_DOM_FRAGMENT so we read text from the returned fragment
 * without creating a separate DOM node (see DOMPurify docs: allow-lists / return-type).
 */
export const normalizeRichTextContent = (content: string | null): string => {
  if (isEmpty(content) || isNil(content)) {
    return '';
  }

  if (!isString(content)) {
    return '';
  }

  const fragment = DOMPurify.sanitize(content, { RETURN_DOM_FRAGMENT: true });
  const textContent = fragment.textContent ?? '';
  return textContent.trim();
};

/**
 * Formats a number into Indian numbering system.
 *
 * Examples:
 * 138623.7580 -> 1,38,623.758
 * 1000000 -> 10,00,000
 * 1234 -> 1,234
 */
export const formatIndianNumber = (
  value: number | string,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  },
): string => {
  if (isNil(value)) {
    return '';
  }

  const numberValue = typeof value === 'string' ? parseFloat(value) : value;

  if (Number.isNaN(numberValue)) {
    return '';
  }

  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }).format(numberValue);
};

export const formatMeasurement = (
  value: number | string | null,
  unit: string,
) => (isNil(value) ? '-' : `${formatIndianNumber(value)} ${unit}`);
